import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GoogleMap } from '@react-google-maps/api';
import {
  arrayRemove,
  arrayUnion,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { auth, db } from '../../../firebase';
import { useLanguage } from '../../../context/LanguageContext.jsx';
import { TranslatedText } from '../../i18n/TranslatedText.jsx';
import './EventDetail.css';

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { isEnglish } = useLanguage();

  const [event, setEvent] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isRegistered, setIsRegistered] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const snap = await getDoc(doc(db, 'events', eventId));
        if (snap.exists()) setEvent({ id: snap.id, ...snap.data() });
      } catch (error) {
        console.error('Error fetching event:', error);
      }
    };
    fetchEvent();
  }, [eventId]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user || !Array.isArray(event?.registeredUsers)) {
        setIsRegistered(false);
        return;
      }
      setIsRegistered(event.registeredUsers.includes(user.uid));
    });
    return () => unsubscribe();
  }, [event?.registeredUsers]);

  useEffect(() => {
    return () => {
      if (markerRef.current) markerRef.current.setMap(null);
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google || !event?.coordinates) return;

    if (markerRef.current) markerRef.current.setMap(null);

    try {
      markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat: event.coordinates.lat, lng: event.coordinates.lng },
        map: mapRef.current,
      });
    } catch (error) {
      console.error('Error creating marker:', error);
    }
  }, [event?.coordinates]);

  const handleNext = () => {
    const len = Array.isArray(event?.mediaUrls) ? event.mediaUrls.length : 0;
    if (len <= 1) return;
    setCurrentMediaIndex((prev) => (prev + 1) % len);
  };

  const handlePrev = () => {
    const len = Array.isArray(event?.mediaUrls) ? event.mediaUrls.length : 0;
    if (len <= 1) return;
    setCurrentMediaIndex((prev) => (prev - 1 + len) % len);
  };

  const registerForEvent = async () => {
    if (!auth.currentUser) return;
    const eventRef = doc(db, 'events', eventId);
    const userRef = doc(db, 'users', auth.currentUser.uid);
    const registrationRef = doc(db, 'events', eventId, 'registrations', auth.currentUser.uid);

    await updateDoc(eventRef, { registeredUsers: arrayUnion(auth.currentUser.uid) });
    await updateDoc(userRef, { registeredEvents: arrayUnion(eventId) });

    try {
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : {};
      await setDoc(
        registrationRef,
        {
          userId: auth.currentUser.uid,
          username: userData?.username ?? null,
          age: userData?.age ?? null,
          email: auth.currentUser.email ?? null,
          registeredAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Error saving registration info:', error);
    }

    setIsRegistered(true);
  };

  const cancelRegistration = async () => {
    if (!auth.currentUser) return;
    const eventRef = doc(db, 'events', eventId);
    const userRef = doc(db, 'users', auth.currentUser.uid);
    const registrationRef = doc(db, 'events', eventId, 'registrations', auth.currentUser.uid);

    await updateDoc(eventRef, { registeredUsers: arrayRemove(auth.currentUser.uid) });
    await updateDoc(userRef, { registeredEvents: arrayRemove(eventId) });

    try {
      await deleteDoc(registrationRef);
    } catch (error) {
      console.error('Error deleting registration info:', error);
    }

    setIsRegistered(false);
    setShowCancelModal(false);
  };

  const handleRegistration = async () => {
    if (!auth.currentUser) {
      setShowSignInModal(true);
      return;
    }
    if (isRegistered) {
      setShowCancelModal(true);
      return;
    }
    await registerForEvent();
  };

  if (!event) return <div>{isEnglish ? 'Loading...' : 'Yükleniyor...'}</div>;

  const isPending = event.status === 'pending';
  const mediaUrls = Array.isArray(event.mediaUrls) ? event.mediaUrls : [];
  const currentUrl = mediaUrls[currentMediaIndex] || '';
  const isVideo = typeof currentUrl === 'string' && currentUrl.includes('.mp4');

  return (
    <div className="event-detail">
      <div className="media-previews">
        <button className="nav-button" onClick={handlePrev} aria-label={isEnglish ? 'Previous' : 'Önceki'}>
          <ArrowLeft size={18} aria-hidden="true" focusable="false" />
        </button>

        {mediaUrls.length > 0 && (
          <div className="media-containers">
            {isVideo ? (
              <video className="media-items" controls>
                <source src={currentUrl} />
              </video>
            ) : (
              <img src={currentUrl} alt={event.name} className="media-items" />
            )}
          </div>
        )}

        <button className="nav-button" onClick={handleNext} aria-label={isEnglish ? 'Next' : 'Sonraki'}>
          <ArrowRight size={18} aria-hidden="true" focusable="false" />
        </button>
      </div>

      <div className="map-containers">
        <GoogleMap
          onLoad={(map) => {
            mapRef.current = map;
            if (!event?.coordinates || !window.google) return;
            try {
              markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
                position: { lat: event.coordinates.lat, lng: event.coordinates.lng },
                map,
              });
            } catch (error) {
              console.error('Error creating marker:', error);
            }
          }}
          mapContainerClassName="maps"
          center={event.coordinates}
          zoom={15}
          options={{ mapId: '6100ebed9733d3ab' }}
        />
      </div>

      <h2>
        <TranslatedText text={event.name} dynamic />
      </h2>

      <p>
        <strong>{isEnglish ? 'Organizer:' : 'Organizatör:'}</strong> <TranslatedText text={event.organizer} dynamic />
      </p>
      <p>
        <strong>{isEnglish ? 'Location:' : 'Konum:'}</strong> <TranslatedText text={event.location} dynamic />
      </p>
      <p>
        <strong>{isEnglish ? 'Date:' : 'Tarih:'}</strong> {event.date}
      </p>
      <p>
        <strong>{isEnglish ? 'Capacity:' : 'Kontenjan Sınırı:'}</strong>{' '}
        {event.maxCapacity || (isEnglish ? 'Unlimited' : 'Sınırsız')}
      </p>
      <p>
        <strong>{isEnglish ? 'Age range:' : 'Yaş Aralığı:'}</strong>{' '}
        {!event.ageRange
          ? isEnglish
            ? 'All ages'
            : 'Her Yaşa Uygundur'
          : event.ageRange === 'all'
            ? 'All Ages'
            : event.ageRange}
      </p>

      <h3>{isEnglish ? 'Contact:' : 'İletişim Bilgileri:'}</h3>
      <div className="contact-info">
        {(event.contactInfo || '').split('\n').map((line, index) => (
          <p key={index}>
            <TranslatedText text={line} dynamic />
          </p>
        ))}
      </div>

      <h3>{isEnglish ? 'About This Event' : 'Etkinlik Hakkında'}</h3>
      <div className="event-description">
        {(event.description || '').split('\n').map((line, index) => (
          <p key={index}>
            <TranslatedText text={line} dynamic />
          </p>
        ))}
      </div>

      {!isPending && !event.registrationDisabled && (
        <button
          className={`registration-button ${isRegistered ? 'registered' : ''}`}
          data-text={
            isRegistered
              ? isEnglish
                ? 'Cancel registration'
                : 'Kaydınızı İptal Edin'
              : isEnglish
                ? 'Register'
                : 'Etkinliğe Kaydolun'
          }
          onClick={handleRegistration}
        >
          {isRegistered
            ? isEnglish
              ? 'Cancel registration'
              : 'Kaydınızı İptal Edin'
            : isEnglish
              ? 'Register'
              : 'Etkinliğe Kaydolun'}
        </button>
      )}

      {showSignInModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{isEnglish ? 'Sign in' : 'Giriş Yapın'}</h3>
            <p>{isEnglish ? 'You need to sign in to register for this event.' : 'Etkinlik için giriş yapmanız gerekmektedir'}</p>
            <div className="modal-buttons">
              <button className="sign-in-btn" onClick={() => navigate('/giriş-yap')}>
                {isEnglish ? 'Sign in' : 'Giriş Yap'}
              </button>
              <button className="cancel-btn" onClick={() => setShowSignInModal(false)}>
                {isEnglish ? 'Cancel' : 'İptal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{isEnglish ? 'Cancel registration' : 'Etkinlik Kayıt İptali'}</h3>
            <p>
              {isEnglish
                ? 'Are you sure you want to cancel your registration?'
                : 'Etkinlik kaydınızı iptal etmek istediğinize emin misiniz?'}
            </p>
            <div className="modal-buttons">
              <button className="confirm-cancel-btn" onClick={cancelRegistration}>
                {isEnglish ? 'Yes, cancel' : 'Evet, İptal Edin'}
              </button>
              <button className="keep-registration-btn" onClick={() => setShowCancelModal(false)}>
                {isEnglish ? 'No' : 'Hayır, Kaydınız Kalsın'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;
